import { ReactNode } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import useSubmit from 'src/hooks/useSubmit'
import { TelTypeState, VulnerableCheckState } from 'src/recoil/common'
import styled from 'styled-components'

export interface VulnerableCheckModalProps {
  submit: ReturnType<typeof useSubmit>
  title?: ReactNode
  children?: ReactNode
  confirmText?: ReactNode
  cancelText?: ReactNode
}

const DEFAULT_TITLE = '서비스 안내'
const DEFAULT_CANCEL_TEXT = '취소'

const SKT_TITLE =
  '휴대폰가족보호는 만 75세 이상 고객님의 안전한 서비스 이용을 위해 아래 사항을 충분히 안내합니다.'

const SKT_CHILDREN = (
  <>
    본 서비스는 월 3,300원(VAT포함)의 유료서비스이며, 복잡한 디지털 조작이 필요할 수 있습니다.
    <br />
    언제든지 해지 가능합니다.
  </>
)

const SKT_CONFIRM = <>계속 진행하기</>
const SKT_CANCEL = <>중단하기</>

const KT_CHILDREN = (
  <>
    휴대폰을 통해 가족 구성원의 상태를 확인하고 도움이 필요한 상황을 알려 안전하게 보호받을 수 있는
    <span style={{ color: '#FF4E4E' }}> 월 3,300원(VAT포함) 유료부가서비스</span>
    입니다.
    <br />
    가입을 진행하시겠습니까?
  </>
)

const KT_CONFIRM = (
  <>
    휴대폰가족보호
    <br />
    유료가입(월 3,300원)
  </>
)

const LGUP_TITLE = '안내사항'

const LgupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
`

const LgupSubTitle = styled.p`
  color: #ff4e4e;
  font-weight: 700;
`

const LgupNotice = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #25282e;
`

const LGUP_CHILDREN = (
  <LgupContainer>
    <LgupSubTitle>유료 휴대폰가족보호 부가서비스 가입 확인</LgupSubTitle>
    <p>
      휴대폰을 통해 가족 구성원의 상태를 확인하고 도움이 필요한 상황을 알려 보호받을 수 있으며
      [동의]를 누르실 경우, 월 3,300원(VAT포함) 서비스 이용 요금이 부과되는 유료서비스 입니다.
    </p>
    <LgupNotice>동의 시 통신요금에 서비스 이용요금이 청구됩니다.</LgupNotice>
  </LgupContainer>
)

const LGUP_CONFIRM = <>동의</>

const VulnerableCheckModal = ({
  submit,
  title = DEFAULT_TITLE,
  children,
  confirmText,
  cancelText = DEFAULT_CANCEL_TEXT,
}: VulnerableCheckModalProps) => {
  const [vulnerableCheck, setVulnerableCheck] = useRecoilState(VulnerableCheckState)
  const telecomType = useRecoilValue(TelTypeState)

  let modalTitle = title
  let modalChildren = children
  let modalConfirmText = confirmText
  let modalCancelText = cancelText

  switch (telecomType) {
    case 0:
      modalTitle = SKT_TITLE
      modalChildren = SKT_CHILDREN
      modalCancelText = SKT_CANCEL
      modalConfirmText = SKT_CONFIRM
      break
    case 1:
      modalChildren = KT_CHILDREN
      modalConfirmText = KT_CONFIRM
      break
    case 2:
      modalTitle = LGUP_TITLE
      modalChildren = LGUP_CHILDREN
      modalConfirmText = LGUP_CONFIRM
      break
    default:
      modalChildren = children
      modalConfirmText = confirmText
  }
  const handleCancel = () => {
    submit.vulnerableModal.close(false)
    setVulnerableCheck(false)
  }

  const handleConfirm = () => {
    submit.vulnerableModal.close(true)
    setVulnerableCheck(false)
  }

  if (!submit.vulnerableModal.isOpen || !vulnerableCheck) return null

  return (
    <ModalOverlay role="presentation">
      <ModalPopup
        role="dialog"
        aria-modal="true"
        aria-labelledby="vulnerable-check-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalTitle id="vulnerable-check-modal-title">{modalTitle}</ModalTitle>
        <ModalContent>{modalChildren}</ModalContent>

        <ModalActions>
          <ModalBtn type="button" variant="cancel" onClick={handleCancel}>
            {modalCancelText}
          </ModalBtn>

          <ModalBtn type="button" variant="confirm" onClick={handleConfirm}>
            {modalConfirmText}
          </ModalBtn>
        </ModalActions>
      </ModalPopup>
    </ModalOverlay>
  )
}

export default VulnerableCheckModal

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
`

const ModalPopup = styled.div`
  display: flex;
  max-width: 450px;
  width: 100%;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  @media all and (max-width: 570px) {
    width: 328px;
  }
`

const ModalTitle = styled.div`
  color: #25282e;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  width: 100%;
`

const ModalContent = styled.div`
  color: #313338;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  overflow-wrap: break-word;
  word-break: keep-all;
  letter-spacing: -0.02rem;
`

const ModalActions = styled.div`
  display: flex;
  width: 100%;
  gap: 6px;
`

const ModalBtn = styled.button<{ variant: 'cancel' | 'confirm' }>`
  min-height: 48px;
  display: flex;
  padding: ${(p) => (p.variant === 'confirm' ? '4px 16px' : '4px 18px')};
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.6;
  cursor: pointer;

  ${(p) =>
    p.variant === 'cancel'
      ? `
    border: 1px solid #CCD0D8;
    background: #fff;
    color: #717580;
    font-weight: 500;
  `
      : `
    border: none;
    background: #000;
    color: #fff;
    font-weight: 700;
    letter-spacing: -0.04rem;
  `}
`
